import React, { forwardRef, useCallback, useEffect } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { styled } from '@compiled/react';

import { Box } from '@atlaskit/primitives/compiled/box';
import type { NewCoreIconProps } from '@atlaskit/icon/base-new';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { FeedType } from '../useFeedTypeSelector.ts';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-html-button -- Ignored via go/DSP-18766
const FeedTypePillButton = styled.button<{
	selected: boolean;
}>({
	outline: 'none',
	boxSizing: 'border-box',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props) =>
		props.selected ? token('color.background.selected') : token('color.background.neutral'),
	display: 'inline-flex',
	font: token('font.body'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: (props) => (props.selected ? token('color.text.brand') : token('color.text')),
	marginTop: '0px',
	userSelect: 'none',
	maxWidth: '100%',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	verticalAlign: 'top',
	cursor: 'pointer',
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		backgroundColor: (props) =>
			props.selected
				? token('color.background.selected.hovered')
				: token('color.background.neutral.hovered'),
	},
	padding: token('space.100'),
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	border: (props) =>
		props.selected
			? // TODO: Change B400 here to B700 when it becomes available
				`${token('border.width')} solid ${token('color.text.selected')}`
			: `${token('border.width')} solid ${token('color.background.neutral')}`,
	borderRadius: token('radius.full'),
	height: '32px',
	'&:focus': {
		outline: `${token('border.width.focused')} solid ${token('color.border.focused')}`,
		borderColor: 'transparent',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	display: 'flex',
	height: '100%',
	alignItems: 'center',
	marginRight: token('space.050'),
});

type FeedTypePillProps = {
	feedType: FeedType | string;
	selectedFeedType: FeedType | string;
	setFeedType: (newFeedType: FeedType | string) => void;
	Icon?: (props: NewCoreIconProps) => JSX.Element;
	children?: ReactNode;
	isPulseActive?: boolean;
	onPulseClick?: () => void;
};

export const FeedTypePill: React.ForwardRefExoticComponent<
	FeedTypePillProps & React.RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, FeedTypePillProps>(
	(
		{
			feedType,
			selectedFeedType,
			setFeedType,
			Icon,
			children,
			isPulseActive = false,
			onPulseClick = () => {},
		},
		ref,
	) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const handleClick = useCallback(() => {
			if (selectedFeedType !== feedType) {
				setFeedType(feedType);
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'feedTypeSelector',
						actionSubjectId: feedType.toLowerCase(),
						source: 'homeScreen',
						attributes: {
							homeVersion: 'visualRefresh',
						},
					},
				}).fire();
			}
		}, [feedType, selectedFeedType, setFeedType, createAnalyticsEvent]);

		const handleMouseDown = useCallback((event: MouseEvent<HTMLElement>) => {
			// prevent focus
			event.preventDefault();
		}, []);

		// Fire screen event when pulse is active
		useEffect(() => {
			if (isPulseActive) {
				createAnalyticsEvent({
					type: 'sendScreenEvent',
					data: {
						name: 'feedTypeSelectorPulse',
						attributes: {
							feedType: feedType.toLowerCase(),
						},
					},
				}).fire();
			}
		}, [isPulseActive, feedType, createAnalyticsEvent]);

		const handlePulseClick = useCallback(() => {
			if (isPulseActive) {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'feedTypeSelectorPulse',
						attributes: {
							feedType: feedType.toLowerCase(),
							homeVersion: 'visualRefresh',
						},
						source: 'homeScreen',
					},
				}).fire();
				onPulseClick();
			}
		}, [feedType, isPulseActive, onPulseClick, createAnalyticsEvent]);

		const selected = selectedFeedType === feedType;
		const testId = `${feedType.toLowerCase()}-feed-button`;
		return (
			<>
				{/* eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable */}
				<Box padding="space.050" onClick={handlePulseClick} role="none">
					<FeedTypePillButton
						selected={selected}
						role="tab"
						id={`tab-${feedType.toLowerCase()}`}
						aria-selected={selected}
						onClick={handleClick}
						onMouseDown={handleMouseDown}
						data-testid={testId}
						ref={ref}
					>
						{Icon && (
							<IconWrapper>
								<Icon
									label=""
									color={selected ? token('color.text.selected') : token('color.text')}
								/>
							</IconWrapper>
						)}
						{children}
					</FeedTypePillButton>
				</Box>
			</>
		);
	},
);
